
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import SearchBar from '../../components/base/SearchBar';
import { supabase } from '../../lib/supabase';
import { useSearchParams } from 'react-router-dom';

type VerificationItem = {
  id: number;
  title: string;
  content: string;
  source?: string;
  questioner?: string;
  status?: string;
  category?: string;
  created_at?: string;
  date?: string;
  verification_type?: string;
  current_status?: string;
  data_type?: string;
  priority?: number; // 重要度追加
};

const VerificationPage = () => {
  const [verificationData, setVerificationData] = useState<VerificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [copyFeedback, setCopyFeedback] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [searchParams] = useSearchParams();

  // SEO設定
  useEffect(() => {
    document.title = "斎藤元彦検証データ - 兵庫県政検証サイト";
    
    // メタタグの設定
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute('content', '兵庫県知事斎藤元彦に関する事実確認と検証情報。未回答事項、虚偽・誇張、関連動向を整理して提供しています。');
    
    // Schema.org JSON-LD for Verification Page
    const verificationPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "斎藤元彦検証データ",
      "description": "兵庫県知事斎藤元彦に関する事実確認と検証情報",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/verification`,
      "inLanguage": "ja",
      "isPartOf": {
        "@type": "WebSite",
        "name": "兵庫県政検証サイト",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      },
      "mainEntity": {
        "@type": "Dataset",
        "name": "斎藤元彦検証データ",
        "description": "県知事に関する検証データの集合",
        "keywords": ["検証", "ファクトチェック", "県政", "斎藤元彦"]
      }
    };
    
    let existingScript = document.querySelector('script[data-schema="verification"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'verification');
    script.textContent = JSON.stringify(verificationPageSchema);
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.querySelector('script[data-schema="verification"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // URLパラメータからカテゴリーとハイライトIDを設定
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  // Fetch verification data and set up realtime subscription
  useEffect(() => {
    const fetchVerificationData = async () => {
      try {
        const { data, error } = await supabase
          .from('verification_data')
          .select('*')
          .order('date', { ascending: false });

        if (error) {
          console.error('Verification data fetch error:', error);
        } else {
          console.log('取得した検証データ:', data?.length, '件');
          setVerificationData(data || []);
          setLoading(false);
        }
      } catch (error) {
        console.error('Data fetch error:', error);
        setLoading(false);
      }
    };

    fetchVerificationData();

    const subscription = supabase
      .channel('verification_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'verification_data' },
        (payload) => {
          console.log('リアルタイム更新:', payload);
          fetchVerificationData();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // ハイライト対象のアイテムを最初に表示するためのソート処理
  const highlightId = searchParams.get('highlight');
  
  const filteredData = verificationData
    .filter((item) => {
      const matchesCategory =
        selectedCategory === 'all' ||
        item.category === selectedCategory ||
        item.data_type === selectedCategory ||
        (selectedCategory === 'unanswered' &&
          (item.data_type === 'unanswered' || item.category === '未回答事項')) ||
        (selectedCategory === 'lies' &&
          (item.data_type === 'lies' || item.category === '虚偽・誇張')) ||
        (selectedCategory === 'movements' &&
          (item.data_type === 'movements' || item.category === '関連動向'));

      const matchesSearch =
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      // ハイライト対象のアイテムを最初に表示
      if (highlightId) {
        if (a.id.toString() === highlightId) return -1;
        if (b.id.toString() === highlightId) return 1;
      }
      
      switch (sortBy) {
        case 'oldest':
          return (
            new Date(a.date || a.created_at).getTime() -
            new Date(b.date || b.created_at).getTime()
          );
        case 'title':
          return a.title.localeCompare(b.title, 'ja');
        case 'priority':
          const priorityA = a.priority || 0;
          const priorityB = b.priority || 0;
          return priorityB - priorityA; // 高い重要度から順
        case 'newest':
        default:
          return (
            new Date(b.date || b.created_at).getTime() -
            new Date(a.date || a.created_at).getTime()
          );
      }
    });

  const categoryCounts = verificationData.reduce((acc, item) => {
    // data_typeとcategoryの両方でカウント
    if (item.data_type === 'unanswered' || item.category === '未回答事項') {
      acc['未回答事項'] = (acc['未回答事項'] || 0) + 1;
    }
    if (item.data_type === 'lies' || item.category === '虚偽・誇張') {
      acc['虚偽・誇張'] = (acc['虚偽・誇張'] || 0) + 1;
    }
    if (item.data_type === 'movements' || item.category === '関連動向') {
      acc['関連動向'] = (acc['関連動向'] || 0) + 1;
    }

    return acc;
  }, {} as Record<string, number>);

  const handleCopyLink = (item: VerificationItem) => {
    const url = `${window.location.origin}/verification#item-${item.id}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopyFeedback(`「${item.title}」のリンクをコピーしました`);
        setTimeout(() => setCopyFeedback(''), 3000);
      })
      .catch((err) => {
        console.error('Clipboard write failed:', err);
        setCopyFeedback('リンクのコピーに失敗しました');
        setTimeout(() => setCopyFeedback(''), 3000);
      });
  };

  const handleShareToX = (item: VerificationItem) => {
    const url = `${window.location.origin}/verification#item-${item.id}`;
    const text = `斎藤元彦検証データ: ${item.title}`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text
    )}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank');
  };

  const handleShareToFacebook = (item: VerificationItem) => {
    const url = `${window.location.origin}/verification#item-${item.id}`;
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url
    )}`;
    window.open(shareUrl, '_blank');
  };

  const handleShareToLine = (item: VerificationItem) => {
    const url = `${window.location.origin}/verification#item-${item.id}`;
    const text = `斎藤元彦検証データ: ${item.title}`;
    const shareUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(
      url
    )}&text=${encodeURIComponent(text)}`;
    window.open(shareUrl, '_blank');
  };

  // データタイプの表示名を取得
  const getDataTypeName = (item: VerificationItem) => {
    if (item.data_type === 'unanswered' || item.category === '未回答事項') {
      return '未回答事項';
    } else if (item.data_type === 'lies' || item.category === '虚偽・誇張') {
      return '虚偽・誇張';
    } else if (item.data_type === 'movements' || item.category === '関連動向') {
      return '関連動向';
    }
    return item.category || '未分類';
  };

  // データタイプの色を取得
  const getDataTypeColor = (item: VerificationItem) => {
    if (item.data_type === 'unanswered' || item.category === '未回答事項') {
      return 'bg-red-100 text-red-800 border-red-200';
    } else if (item.data_type === 'lies' || item.category === '虚偽・誇張') {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    } else if (item.data_type === 'movements' || item.category === '関連動向') {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // 重要度の表示
  const getPriorityDisplay = (priority?: string) => {
    if (!priority) return null;
    
    if (priority === 'S') {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
        <i className="ri-error-warning-line mr-1"></i>S: 最重要
      </span>;
    } else if (priority === 'A') {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
        <i className="ri-alert-line mr-1"></i>A: 重要
      </span>;
    } else if (priority === 'B') {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
        <i className="ri-information-line mr-1"></i>B: 普通
      </span>;
    } else if (priority === 'C') {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
        <i className="ri-subtract-line mr-1"></i>C: 低
      </span>;
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="flex justify-center items-center">
            <div className="text-lg">データを読み込み中...</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Copy Feedback */}
      {copyFeedback && (
        <div className="fixed top-20 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {copyFeedback}
        </div>
      )}

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">斎藤元彦検証データ</h1>
            <p className="text-xl mb-8">
              兵庫県知事に関する事実確認と検証情報
            </p>

            {/* Main Statistics */}
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              {[
                {
                  category: '答えていないこと',
                  count: categoryCounts['未回答事項'] || 0,
                  color: 'bg-red-400 hover:bg-red-300',
                  key: 'unanswered'
                },
                {
                  category: '虚偽・誇張',
                  count: categoryCounts['虚偽・誇張'] || 0,
                  color: 'bg-orange-500 hover:bg-orange-400',
                  key: 'lies'
                },
                {
                  category: '関連動向',
                  count: categoryCounts['関連動向'] || 0,
                  color: 'bg-blue-500 hover:bg-blue-400',
                  key: 'movements'
                },
              ].map((stat, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedCategory(stat.key)}
                  className={`${stat.color} rounded-lg p-6 text-center transition-all duration-300 cursor-pointer transform hover:scale-105 shadow-lg hover:shadow-xl`}
                >
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-5xl font-bold text-white">{stat.count}</span>
                    <span className="text-lg ml-1 text-white">件</span>
                  </div>
                  <div className="text-base font-semibold text-white">{stat.category}</div>
                </button>
              ))}
            </div>

          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg p-6 mb-8 shadow-sm border">
          <div className="mb-6">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="検証データを検索..."
            />
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">並び替え:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setSortBy('newest')}
                className={`px-3 py-1 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  sortBy === 'newest'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                新着順
              </button>
              <button
                onClick={() => setSortBy('oldest')}
                className={`px-3 py-1 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  sortBy === 'oldest'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                古い順
              </button>
              <button
                onClick={() => setSortBy('priority')}
                className={`px-3 py-1 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  sortBy === 'priority'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                重要度順
              </button>
              <button
                onClick={() => setSortBy('title')}
                className={`px-3 py-1 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  sortBy === 'title'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                タイトル順
              </button>
            </div>
          </div>
        </div>

        {/* Data List */}
        <div className="space-y-6">
          {filteredData.map((item, index) => (
            <Card
              key={item.id}
              className={`hover:shadow-lg transition-shadow bg-white border ${
                highlightId && item.id.toString() === highlightId 
                  ? 'ring-2 ring-blue-500 border-blue-300 bg-blue-50' 
                  : ''
              }`}
            >
              <div id={`item-${item.id}`} className="scroll-mt-20 p-6">
                {/* ハイライト表示用のバッジ */}
                {highlightId && item.id.toString() === highlightId && (
                  <div className="mb-4 flex items-center gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      <i className="ri-focus-line mr-1"></i>
                      ホームから選択された項目
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-4">
                    {/* 目立つ日付表示（情報更新日） */}
                    <div className="bg-red-100 border-2 border-red-300 rounded-lg p-3 text-center min-w-[80px]">
                      <div className="text-xs text-red-600 font-medium">
                        {new Date(item.date || item.created_at).toLocaleDateString('ja-JP', { month: 'short' })}
                      </div>
                      <div className="text-2xl font-bold text-red-800">
                        {new Date(item.date || item.created_at).getDate()}
                      </div>
                      <div className="text-xs text-red-600">
                        {new Date(item.date || item.created_at).getFullYear()}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getDataTypeColor(item)}`}>
                          {getDataTypeName(item)}
                        </span>
                        {getPriorityDisplay(item.priority)}
                      </div>
                    </div>
                  </div>

                  {/* Share Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopyLink(item)}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors whitespace-nowrap text-gray-700"
                      title="リンクをコピー"
                    >
                      <i className="ri-link text-sm"></i>
                      <span className="hidden sm:inline">コピー</span>
                    </button>
                    <button
                      onClick={() => handleShareToX(item)}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800 rounded-lg transition-colors whitespace-nowrap"
                      title="Xで共有"
                    >
                      <i className="ri-twitter-x-line text-sm"></i>
                      <span className="hidden sm:inline">X</span>
                    </button>
                    <button
                      onClick={() => handleShareToFacebook(item)}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"
                      title="Facebookで共有"
                    >
                      <i className="ri-facebook-line text-sm"></i>
                      <span className="hidden sm:inline">FB</span>
                    </button>
                    <button
                      onClick={() => handleShareToLine(item)}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-green-500 text-white hover:bg-green-600 rounded-lg transition-colors whitespace-nowrap"
                      title="LINEで共有"
                    >
                      <i className="ri-line-line text-sm"></i>
                      <span className="hidden sm:inline">LINE</span>
                    </button>
                  </div>
                </div>

                {/* ソースと質問者をタイトルの上に表示 */}
                <div className="mb-3 space-y-1">
                  {item.source && (
                    <div className="text-sm text-gray-600">
                      <strong className="text-gray-800">初出:</strong> {item.source}
                    </div>
                  )}
                  {item.questioner && getDataTypeName(item) !== '関連動向' && (
                    <div className="text-sm text-gray-600">
                      <strong className="text-gray-800">質問者:</strong> {item.questioner}
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">{item.title}</h3>
                <div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed mb-4">
                  {item.content}
                </div>

                {item.status && getDataTypeName(item) !== '関連動向' && (
                  <div className="text-sm text-gray-800 border-t pt-3 bg-gray-50 -mx-6 px-6 py-3 mt-4">
                    <strong className="text-gray-900">当初ステータス:</strong>{' '}
                    {item.status}
                  </div>
                )}

                {item.verification_type && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4 rounded-r-lg">
                    <div className="text-sm font-medium text-blue-800 mb-2">
                      今後すべきアクション:
                    </div>
                    <p className="text-sm text-blue-700 whitespace-pre-wrap">
                      {item.verification_type}
                    </p>
                  </div>
                )}

                {/* Share Call-to-Action */}
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4 rounded-r-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        この情報の拡散にご協力ください
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        多くの方に知っていただくため、SNSでの共有をお願いします
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleShareToX(item)}
                        className="flex items-center gap-1 px-2 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-twitter-x-line"></i>
                        拡散
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {filteredData.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              該当するデータが見つかりませんでした。
            </p>
          </div>
        )}

        {/* Call to Action */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 mt-12 text-center">
          <h3 className="text-2xl font-bold text-red-800 mb-4">
            情報の拡散にご協力ください
          </h3>
          <p className="text-red-700 mb-6">
            これらの検証データは多くの方に知っていただくべき重要な情報です。
            <br />
            SNSでの共有や知人への紹介など、情報拡散にご協力をお願いいたします。
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                const url = window.location.href;
                const text =
                  '斎藤元彦検証データ - 兵庫県知事に関する事実確認と検証情報';
                const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  text
                )}&url=${encodeURIComponent(url)}`;
                window.open(shareUrl, '_blank');
              }}
              className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-twitter-x-line"></i>
              Xで全体を共有
            </button>
            <button
              onClick={() => {
                const url = window.location.href;
                navigator.clipboard
                  .writeText(url)
                  .then(() => {
                    setCopyFeedback('ページリンクをコピーしました');
                    setTimeout(() => setCopyFeedback(''), 3000);
                  })
                  .catch((err) => {
                    console.error('Clipboard failed:', err);
                    setCopyFeedback('ページリンクのコピーに失敗しました');
                    setTimeout(() => setCopyFeedback(''), 3000);
                  });
              }}
              className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-link"></i>
              ページリンクをコピー
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VerificationPage;
